// src/analytics.ts
var CONSENT_SCRIPT_URL = process.env.NEXT_PUBLIC_CONSENT_SCRIPT_URL;
var CONSENT_PRESET = process.env.NEXT_PUBLIC_CONSENT_PRESET;
var IS_DEBUG = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG || false;
var Analytics = class _Analytics {
  static affiliation;
  static subaffiliation;
  static platform;
  static timeLoaded = (/* @__PURE__ */ new Date()).getTime();
  static retryInterval = 100;
  static retryMaxMs = 1e4;
  static eventMaxMs = 2e3;
  static isDebugMode = IS_DEBUG === true || IS_DEBUG === "true";
  // ______________________________________________________________
  // INITIALIZATION (should only be called once)
  static async init({
    affiliation,
    subaffiliation,
    platform = "Web",
    simulateEU: isSimulateEU = false,
    config
  }) {
    _Analytics.affiliation = affiliation;
    _Analytics.subaffiliation = subaffiliation;
    _Analytics.platform = platform;
    const url = CONSENT_SCRIPT_URL || "https://consent.a24films.com/v1.0/a24-consent.js";
    const preset = CONSENT_PRESET;
    return new Promise((resolve, reject) => {
      try {
        if (isSimulateEU) {
          window.OneTrust = window.OneTrust || {};
          window.OneTrust = {
            geolocationResponse: {
              countryCode: "DE",
              regionCode: "EU"
            }
          };
        }
        const script = document.createElement("script");
        script.onload = () => {
          new window.A24Consent({
            ...preset ? { preset } : {},
            ...config ? { ...config } : {}
          });
          _Analytics.timeLoaded = (/* @__PURE__ */ new Date()).getTime();
          resolve();
        };
        script.onerror = (error) => {
          _Analytics.retryMaxMs = 0;
          _Analytics.eventMaxMs = 0;
          _Analytics.error("init", error);
          reject(error);
        };
        script.src = url;
        script.async = true;
        document.body.appendChild(script);
      } catch (error) {
        reject(error);
      }
    });
  }
  // ______________________________________________________________
  // GETTERS
  static get consent() {
    return window.A24Consent;
  }
  static get onetrust() {
    return window.OneTrust;
  }
  static get rudder() {
    return window.rudderanalytics;
  }
  static get msSinceLoad() {
    return (/* @__PURE__ */ new Date()).getTime() - _Analytics.timeLoaded;
  }
  // ________________________________________________________________
  // EVENT METHODS
  static identify = async ({
    userId,
    traits
  }) => {
    await _Analytics.rudderReady();
    return new Promise((resolve) => {
      var _a;
      const payload = _Analytics.createPayload(traits);
      const timeout = setTimeout(resolve, _Analytics.eventMaxMs);
      try {
        (_a = _Analytics.rudder) == null ? void 0 : _a.identify(userId, payload, {}, () => {
          clearTimeout(timeout);
          resolve();
        });
      } catch (error) {
        _Analytics.error("identify", error);
        resolve();
      }
    });
  };
  static page = async ({
    title,
    props
  }) => {
    await _Analytics.rudderReady();
    return new Promise((resolve) => {
      var _a;
      const pageTitle = title || document.title;
      const payload = _Analytics.createPayload(props);
      const timeout = setTimeout(resolve, _Analytics.eventMaxMs);
      try {
        (_a = _Analytics.rudder) == null ? void 0 : _a.page(pageTitle, payload, {}, () => {
          clearTimeout(timeout);
          resolve();
        });
      } catch (error) {
        _Analytics.error("page", error);
        resolve();
      }
    });
  };
  static track = async (event, { props }) => {
    await _Analytics.rudderReady();
    return new Promise((resolve) => {
      var _a;
      const payload = _Analytics.createPayload(props);
      const timeout = setTimeout(resolve, _Analytics.eventMaxMs);
      try {
        (_a = _Analytics.rudder) == null ? void 0 : _a.track(event, payload, {}, () => {
          clearTimeout(timeout);
          resolve();
        });
      } catch (error) {
        _Analytics.error("track", error);
        resolve();
      }
    });
  };
  // ________________________________________________________________
  // CONSENT SCRIPT METHODS
  static openConsentPrefs = () => {
    var _a;
    (_a = _Analytics.consent) == null ? void 0 : _a.togglePreferenceCenter();
  };
  // ______________________________________________________________
  // UTILITY METHODS
  static createPayload = (propsOrTraits) => {
    return {
      platform: _Analytics.platform,
      affiliation: _Analytics.affiliation,
      ..._Analytics.subaffiliation ? { subaffiliation: _Analytics.subaffiliation } : {},
      ...propsOrTraits || {}
    };
  };
  static rudderReady = () => {
    return new Promise((resolve) => {
      const check = () => {
        var _a;
        if ((_a = _Analytics.consent) == null ? void 0 : _a.state.rudderstack.isReady) {
          resolve(true);
        } else if (_Analytics.msSinceLoad > _Analytics.retryMaxMs) {
          resolve(false);
        } else {
          setTimeout(check, _Analytics.retryInterval);
        }
      };
      check();
    });
  };
  static error = (message, error) => {
    if (_Analytics.isDebugMode) {
      console.error(message, error);
    }
  };
};

export {
  Analytics
};
